import React, { useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, CheckCircle2, File, FolderOpen, Loader2, XCircle } from 'lucide-react';
import DialogBase from '../../../../base/DialogBase/DialogBase';
import { useSuccessAlert } from '../../../../App.hooks';
import { FormAutoComplete, FormField, FormTextArea } from '../../../form';
import RequestTagSelector from '../../../main/RequestTagSelector/RequestTagSelector';
import {
    buildPreview, getFileStem, parseFolderFile, ParsedItem, PreviewItem,
} from '../ContentRequestDialog/contentRequestPreview';

const GET_BULK_REQUEST_SETUP = gql`
    query getBulkRequestSetup {
        ClientList(first: 10000, order: { name: ASC }) {
            nodes { id name defaultLibraryId }
        }
        LibraryList(first: 10000, order: { name: ASC }) {
            nodes { id name clientId }
        }
        ApplicationNameList
        CategoryList
    }
`;

const GET_LIBRARY_CONTENTS = gql`
    query getLibraryContentsForBulkRequests($libraryId: String) {
        Library(id: $libraryId) {
            directContents(first: 10000) {
                nodes {
                    id
                    name
                    category
                    applicationName
                    currentRequestId
                }
            }
        }
        Requests(
            first: 10000
            state: OPEN
            where: {
                libraryId: { eq: $libraryId }
                requestType: { eq: NEW_CONTENT }
            }
        ) {
            nodes {
                id
                name
                category
                applicationName
                requestType
                state
            }
        }
    }
`;

const CREATE_BULK_CONTENT_REQUEST = gql`
    mutation createBulkContentRequest($request: CreateContentRequestInput, $file: Upload, $message: String) {
        Request(request: $request, file: $file, message: $message)
    }
`;

const SUBMIT_BULK_REQUEST_DRAFT = gql`
    mutation submitBulkRequestDraft($id: String!, $fileName: String!, $message: String) {
        SubmitDraft(id: $id, fileName: $fileName, message: $message) {
            requestId
            uploadUrl
        }
    }
`;

const UPDATE_BULK_REQUEST_TAGS = gql`
    mutation updateBulkRequestTags($id: String!, $request: EditContentRequestInput, $message: String) {
        EditRequest(id: $id, request: $request, message: $message)
    }
`;

type Phase = 'select' | 'preview' | 'submitting' | 'done';
export type ContentRequestMode = 'single' | 'folder';

type ClientOption = { id: string; name: string; defaultLibraryId?: string | null };
type LibraryOption = { id: string; name: string; clientId: string };

type Props = {
    visible: boolean;
    onClose: () => void;
    defaultMode?: ContentRequestMode;
};

const ContentRequestDialog = ({ visible, onClose, defaultMode = 'folder' }: Props) => {
    const folderInputRef = useRef<HTMLInputElement>(null);
    const singleInputRef = useRef<HTMLInputElement>(null);
    const apolloClient = useApolloClient();
    const setSuccessAlert = useSuccessAlert();
    const [mode, setMode] = useState<ContentRequestMode>(defaultMode);
    const [phase, setPhase] = useState<Phase>('select');
    const [clientText, setClientText] = useState('');
    const [clientId, setClientId] = useState('');
    const [libraryText, setLibraryText] = useState('');
    const [libraryId, setLibraryId] = useState('');
    const [tagIds, setTagIds] = useState<string[]>([]);
    const [tagsValid, setTagsValid] = useState(false);
    const [missingTagTypes, setMissingTagTypes] = useState<string[]>([]);
    const [applicationName, setApplicationName] = useState('');
    const [message, setMessage] = useState('');
    const [singleFile, setSingleFile] = useState<File | null>(null);
    const [singleName, setSingleName] = useState('');
    const [singleCategory, setSingleCategory] = useState('');
    const [lastDerivedName, setLastDerivedName] = useState('');
    const [folderItems, setFolderItems] = useState<ParsedItem[]>([]);
    const [items, setItems] = useState<PreviewItem[]>([]);
    const [validationError, setValidationError] = useState('');
    const [ignoredCount, setIgnoredCount] = useState(0);

    const setupResult = useQuery(GET_BULK_REQUEST_SETUP, { skip: !visible });
    const [fetchContents, contentsResult] = useLazyQuery(GET_LIBRARY_CONTENTS, { fetchPolicy: 'network-only' });
    const [createRequest] = useMutation(CREATE_BULK_CONTENT_REQUEST);
    const [submitDraft] = useMutation(SUBMIT_BULK_REQUEST_DRAFT);
    const [updateRequestTags] = useMutation(UPDATE_BULK_REQUEST_TAGS);

    const clients: ClientOption[] = useMemo(
        () => (setupResult.data?.ClientList?.nodes ?? []).filter((x: any) => x?.id && x?.name),
        [setupResult.data]
    );
    const selectedClient = clients.find(client => client.id === clientId);
    const libraries: LibraryOption[] = useMemo(
        () => (setupResult.data?.LibraryList?.nodes ?? [])
            .filter((x: any) => x?.id && x?.name && x?.clientId),
        [setupResult.data]
    );
    const clientLibraries = libraries.filter(library => library.clientId === clientId);
    const libraryLabels = clientLibraries.map(library =>
        `${library.name}${library.id === selectedClient?.defaultLibraryId ? ' — Default' : ''}`);
    const applications: string[] = (setupResult.data?.ApplicationNameList ?? []).filter(Boolean);
    const categories: string[] = (setupResult.data?.CategoryList ?? []).filter(Boolean);
    useEffect(() => {
        if (!visible) return;
        setMode(defaultMode);
        setPhase('select');
        setItems([]);
        setFolderItems([]);
        setSingleFile(null);
        setSingleName('');
        setSingleCategory('');
        setLastDerivedName('');
        setValidationError('');
        setIgnoredCount(0);
    }, [visible, defaultMode]);

    const handleClientChange = (text: string) => {
        setClientText(text);
        const match = clients.find(client => client.name === text);
        setClientId(match?.id ?? '');
        setLibraryId('');
        setLibraryText('');

        if (match?.defaultLibraryId) {
            const defaultLibrary = libraries.find(library => library.id === match.defaultLibraryId);
            if (defaultLibrary) {
                setLibraryId(defaultLibrary.id);
                setLibraryText(`${defaultLibrary.name} — Default`);
            }
        }
    };

    const handleLibraryChange = (text: string) => {
        setLibraryText(text);
        const match = clientLibraries.find(library =>
            `${library.name}${library.id === selectedClient?.defaultLibraryId ? ' — Default' : ''}` === text);
        setLibraryId(match?.id ?? '');
    };

    const handleSingleFile = (file: File | null) => {
        setSingleFile(file);
        if (!file) {
            setSingleName('');
            setLastDerivedName('');
            return;
        }
        const derivedName = getFileStem(file.name);
        setSingleName(current => !current.trim() || current === lastDerivedName ? derivedName : current);
        setLastDerivedName(derivedName);
        setValidationError('');
    };

    const handleFolderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setValidationError('');
        const files = Array.from(event.target.files ?? []);
        const parsed = files.map(parseFolderFile).filter((item): item is ParsedItem => item !== null);
        setFolderItems(parsed);
        setIgnoredCount(files.length - parsed.length);
        if (files.length > 0 && parsed.length === 0) {
            setValidationError('No files were found in the expected Category/Content Name.ext structure.');
        }
    };

    const handleModeChange = (nextMode: ContentRequestMode) => {
        setMode(nextMode);
        setSingleFile(null);
        setSingleName('');
        setSingleCategory('');
        setLastDerivedName('');
        setFolderItems([]);
        setIgnoredCount(0);
        setValidationError('');
        if (folderInputRef.current) folderInputRef.current.value = '';
        if (singleInputRef.current) singleInputRef.current.value = '';
    };

    const handleReview = async () => {
        setValidationError('');
        if (!clientId || !libraryId) {
            setValidationError('Select a client and an exact library.');
            return;
        }
        if (!tagsValid) {
            setValidationError(missingTagTypes.length
                ? `Select at least one tag for: ${missingTagTypes.join(', ')}.`
                : 'Tag requirements could not be validated.');
            return;
        }
        if (!applicationName.trim()) {
            setValidationError('Enter or select an application.');
            return;
        }
        if (!message.trim()) {
            setValidationError('Enter a comment for the requests.');
            return;
        }

        let parsed = folderItems;
        if (mode === 'single') {
            if (!singleFile) {
                setValidationError('Choose a content file.');
                return;
            }
            if (!singleName.trim()) {
                setValidationError('Enter a content name.');
                return;
            }
            if (!singleCategory.trim()) {
                setValidationError('Enter or select a category.');
                return;
            }
            parsed = [{
                file: singleFile,
                relativePath: singleFile.name,
                category: singleCategory.trim(),
                contentName: singleName.trim(),
            }];
        }
        if (parsed.length === 0) {
            setValidationError('No files were found in the expected Category/Content Name.ext structure.');
            return;
        }

        try {
            const result = await fetchContents({ variables: { libraryId } });
            const contents = result.data?.Library?.directContents?.nodes ?? [];
            const openNewRequests = result.data?.Requests?.nodes ?? [];
            setItems(buildPreview(parsed, contents, openNewRequests, applicationName));
            setPhase('preview');
        } catch (error: any) {
            setValidationError(error?.message ?? 'The selected library could not be checked.');
        }
    };

    const toggleIncluded = (index: number) => {
        setItems(current => current.map((item, i) =>
            i === index && item.kind !== 'blocked' ? { ...item, included: !item.included } : item));
    };

    const handleSubmit = async () => {
        const selectedCount = items.filter(item => item.included && item.kind !== 'blocked').length;
        if (selectedCount === 0) return;
        setPhase('submitting');
        const updated = [...items];

        for (let index = 0; index < updated.length; index++) {
            const item = updated[index];
            if (!item.included || item.kind === 'blocked') continue;
            updated[index] = { ...item, status: 'submitting', error: undefined };
            setItems([...updated]);

            try {
                if (item.kind === 'open-request') {
                    await updateRequestTags({
                        variables: {
                            id: item.requestId,
                            request: { tagIds },
                            message: 'Tags updated from content request upload.',
                        },
                    });
                    const draftResult = await submitDraft({
                        variables: {
                            id: item.requestId,
                            fileName: item.file.name,
                            message: message.trim(),
                        },
                    });
                    const uploadUrl = draftResult.data?.SubmitDraft?.uploadUrl;
                    if (!uploadUrl) throw new Error('No draft upload URL was returned.');
                    const uploadResponse = await fetch(uploadUrl, {
                        method: 'PUT',
                        headers: { 'x-ms-blob-type': 'BlockBlob' },
                        body: item.file,
                    });
                    if (!uploadResponse.ok) {
                        throw new Error(`Draft upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`);
                    }
                } else {
                    await createRequest({
                        variables: {
                            request: {
                                name: item.contentName,
                                category: item.category,
                                tagIds,
                                clientId,
                                libraryId,
                                contentId: item.kind === 'edit' ? item.contentId : null,
                                applicationName: applicationName.trim(),
                                requestType: item.kind === 'edit' ? 'EDIT_CONTENT' : 'NEW_CONTENT',
                            },
                            file: item.file,
                            message: message.trim(),
                        },
                    });
                }
                updated[index] = { ...updated[index], status: 'done' };
            } catch (error: any) {
                const detail = error?.graphQLErrors?.[0]?.extensions?.message
                    || error?.graphQLErrors?.[0]?.message
                    || error?.networkError?.result?.errors?.[0]?.message
                    || error?.message
                    || 'Request submission failed.';
                updated[index] = { ...updated[index], status: 'error', error: detail };
            }
            setItems([...updated]);
        }

        setPhase('done');
        try {
            await apolloClient.refetchQueries({
                include: ['GetMyRequests', 'GetAssignedRequests', 'GetPendingRequests', 'GetRequests'],
            });
        } catch {
            // Submission results remain authoritative; the user can still refresh
            // manually if a list query is temporarily unavailable.
        }
        if (updated.some(item => item.status === 'done')) setSuccessAlert('Content requests submitted.');
    };

    const reset = () => {
        setPhase('select');
        setItems([]);
        setFolderItems([]);
        setSingleFile(null);
        setSingleName('');
        setSingleCategory('');
        setLastDerivedName('');
        setIgnoredCount(0);
        setValidationError('');
        if (folderInputRef.current) folderInputRef.current.value = '';
        if (singleInputRef.current) singleInputRef.current.value = '';
    };

    const close = () => {
        if (phase === 'submitting') return;
        reset();
        setClientText('');
        setClientId('');
        setLibraryText('');
        setLibraryId('');
        setTagIds([]);
        setTagsValid(false);
        setMissingTagTypes([]);
        setApplicationName('');
        setMessage('');
        onClose();
    };

    const selectedCount = items.filter(item => item.included && item.kind !== 'blocked').length;
    const newCount = items.filter(item => item.kind === 'new').length;
    const editCount = items.filter(item => item.kind === 'edit').length;
    const ignoredOpenRequestCount = items.filter(item => item.kind === 'open-request' && !item.included).length;
    const draftCount = items.filter(item => item.kind === 'open-request' && item.included).length;
    const blockedCount = items.filter(item => item.kind === 'blocked').length;
    const newCategories = Array.from(new Set(items.filter(item => item.newCategory).map(item => item.category)));
    const doneCount = items.filter(item => item.status === 'done').length;
    const errorCount = items.filter(item => item.status === 'error').length;

    const actions = phase === 'select' ? (
        <>
            <button className="btn btn-secondary" onClick={close}>Cancel</button>
            <button className="btn btn-primary" onClick={handleReview}>Review</button>
        </>
    ) : phase === 'preview' ? (
        <>
            <button className="btn btn-secondary" onClick={() => { setPhase('select'); setValidationError(''); }}>Back</button>
            <button className="btn btn-primary" disabled={selectedCount === 0} onClick={handleSubmit}>
                Submit {selectedCount} request{selectedCount === 1 ? '' : 's'}
            </button>
        </>
    ) : phase === 'done' ? (
        <>
            <button className="btn btn-secondary" onClick={reset}>Create More</button>
            <button className="btn btn-primary" onClick={close}>Close</button>
        </>
    ) : null;

    return (
        <DialogBase
            title={phase === 'done' ? 'Content Request Results' : 'Content Requests'}
            type="Custom"
            visible={visible}
            onClose={close}
            actions={actions}
            validationError={validationError}
            width="920px"
            isLoading={setupResult.loading || contentsResult.loading}
        >
            {phase === 'select' && (
                <div className="grid grid-cols-2 gap-x-4">
                    <div className="col-span-2 flex mb-4" role="group" aria-label="Request source">
                        <button
                            type="button"
                            className={mode === 'single' ? 'btn btn-primary' : 'btn btn-secondary'}
                            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            aria-pressed={mode === 'single'}
                            onClick={() => handleModeChange('single')}
                        >
                            <File size={15} /> Single file
                        </button>
                        <button
                            type="button"
                            className={mode === 'folder' ? 'btn btn-primary' : 'btn btn-secondary'}
                            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            aria-pressed={mode === 'folder'}
                            onClick={() => handleModeChange('folder')}
                        >
                            <FolderOpen size={15} /> Folder
                        </button>
                    </div>
                    <FormAutoComplete
                        label="Client"
                        name="bulk-request-client"
                        value={clientText}
                        onChange={handleClientChange}
                        options={clients.map(client => client.name)}
                        placeholder="Select a client"
                    />
                    <div>
                        <FormAutoComplete
                            label="Library"
                            name="bulk-request-library"
                            value={libraryText}
                            onChange={handleLibraryChange}
                            options={libraryLabels}
                            placeholder={clientId ? 'Select a library' : 'Select a client first'}
                            disabled={!clientId}
                        />
                        {selectedClient?.defaultLibraryId && (
                            <p className="text-xs -mt-3 mb-4" style={{ color: 'var(--text-tertiary)' }}>
                                The client default is preselected; you can choose any other library.
                            </p>
                        )}
                    </div>
                    <FormAutoComplete
                        label="Application"
                        name="bulk-request-application"
                        value={applicationName}
                        onChange={setApplicationName}
                        options={applications}
                        placeholder="Select or enter a new application"
                    />
                    {mode === 'single' && (
                        <>
                            <FormField
                                label="Name"
                                name="single-request-name"
                                value={singleName}
                                onChange={setSingleName}
                            />
                            <FormAutoComplete
                                label="Category"
                                name="single-request-category"
                                value={singleCategory}
                                onChange={setSingleCategory}
                                options={categories}
                                placeholder="Select or enter a new category"
                            />
                        </>
                    )}
                    <div className="col-span-2">
                        <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-secondary)' }}>Tags</label>
                        <RequestTagSelector
                            value={tagIds}
                            onChange={setTagIds}
                            onValidityChange={(valid, missing) => {
                                setTagsValid(valid);
                                setMissingTagTypes(missing);
                            }}
                        />
                    </div>
                    <div className="col-span-2">
                        <FormTextArea
                            label="Comment"
                            name="bulk-request-comment"
                            value={message}
                            onChange={setMessage}
                            rows={3}
                        />
                    </div>
                    {mode === 'single' ? (
                        <div
                            className="col-span-2 flex flex-col items-center rounded-lg p-6"
                            style={{ border: '2px dashed var(--border-default)', background: 'var(--bg-subtle)' }}
                            onDragOver={event => event.preventDefault()}
                            onDrop={event => {
                                event.preventDefault();
                                handleSingleFile(event.dataTransfer.files[0] ?? null);
                            }}
                        >
                            <File size={34} className="mb-2" style={{ color: 'var(--text-tertiary)' }} />
                            <p className="text-sm font-medium mb-1">
                                {singleFile ? singleFile.name : 'Choose a content file'}
                            </p>
                            <p className="text-xs text-center mb-4" style={{ color: 'var(--text-tertiary)' }}>
                                Drop a file here or browse. The filename supplies the initial content name.
                            </p>
                            <input
                                ref={singleInputRef}
                                type="file"
                                className="hidden"
                                onChange={event => handleSingleFile(event.target.files?.[0] ?? null)}
                            />
                            <button type="button" className="btn btn-primary" onClick={() => singleInputRef.current?.click()}>
                                <File size={15} /> {singleFile ? 'Replace File' : 'Choose File'}
                            </button>
                        </div>
                    ) : (
                        <div
                            className="col-span-2 flex flex-col items-center rounded-lg p-6"
                            style={{ border: '2px dashed var(--border-default)', background: 'var(--bg-subtle)' }}
                        >
                            <FolderOpen size={34} className="mb-2" style={{ color: 'var(--text-tertiary)' }} />
                            <p className="text-sm font-medium mb-1">
                                {folderItems.length > 0
                                    ? `${folderItems.length} valid file${folderItems.length === 1 ? '' : 's'} selected`
                                    : 'Choose a content folder'}
                            </p>
                            <p className="text-xs text-center mb-4" style={{ color: 'var(--text-tertiary)' }}>
                                Expected structure: <code>Folder\Category\Content Name.ext</code>. The file name becomes the content name.
                            </p>
                            <input
                                ref={folderInputRef}
                                type="file"
                                className="hidden"
                                // @ts-ignore webkitdirectory is supported by the target browsers.
                                webkitdirectory=""
                                multiple
                                onChange={handleFolderChange}
                            />
                            <button type="button" className="btn btn-primary" onClick={() => folderInputRef.current?.click()}>
                                <FolderOpen size={15} /> {folderItems.length > 0 ? 'Replace Folder' : 'Choose Folder'}
                            </button>
                            {ignoredCount > 0 && (
                                <p className="text-xs mt-2" style={{ color: 'var(--warning)' }}>
                                    {ignoredCount} unsupported or incorrectly nested file{ignoredCount === 1 ? '' : 's'} will be ignored.
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}

            {phase === 'preview' && (
                <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>
                            {editCount} edit
                        </span>
                        <span className="px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--accent-soft)', color: 'var(--accent)' }}>
                            {newCount} new
                        </span>
                        {ignoredOpenRequestCount > 0 && (
                            <span className="px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}>
                                {ignoredOpenRequestCount} open request{ignoredOpenRequestCount === 1 ? '' : 's'} ignored
                            </span>
                        )}
                        {draftCount > 0 && (
                            <span className="px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}>
                                {draftCount} draft{draftCount === 1 ? '' : 's'}
                            </span>
                        )}
                        {blockedCount > 0 && (
                            <span className="px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                                {blockedCount} blocked
                            </span>
                        )}
                        {newCategories.length > 0 && (
                            <span
                                className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm"
                                style={{ background: 'var(--warning-soft)', color: 'var(--warning)' }}
                                title={newCategories.join(', ')}
                            >
                                <AlertCircle size={14} />
                                {newCategories.length} new categor{newCategories.length === 1 ? 'y' : 'ies'}
                            </span>
                        )}
                        {ignoredCount > 0 && (
                            <span className="text-xs ml-auto" style={{ color: 'var(--text-tertiary)' }}>
                                {ignoredCount} unsupported or incorrectly nested file{ignoredCount === 1 ? '' : 's'} ignored
                            </span>
                        )}
                    </div>
                    <div className="overflow-auto rounded-lg" style={{ maxHeight: '440px', border: '1px solid var(--border-default)' }}>
                        <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ background: 'var(--bg-subtle)', borderBottom: '1px solid var(--border-default)' }}>
                                    <th className="px-3 py-2 w-10"></th>
                                    <th className="text-left px-3 py-2 text-xs">File</th>
                                    <th className="text-left px-3 py-2 text-xs">Category</th>
                                    <th className="text-left px-3 py-2 text-xs">Request</th>
                                    <th className="text-left px-3 py-2 text-xs">Match detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, index) => (
                                    <tr key={item.relativePath} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td className="px-3 py-2">
                                            {item.kind !== 'open-request' && (
                                                <input
                                                    type="checkbox"
                                                    checked={item.included}
                                                    disabled={item.kind === 'blocked'}
                                                    onChange={() => toggleIncluded(index)}
                                                />
                                            )}
                                        </td>
                                        <td className="px-3 py-2">
                                            <span className="flex items-center gap-2 font-mono text-xs" title={item.relativePath}>
                                                <File size={13} /> {item.contentName}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                            <span className="flex items-center gap-1">
                                                {item.category}
                                                {item.newCategory && (
                                                    <AlertCircle
                                                        size={13}
                                                        style={{ color: 'var(--warning)', flexShrink: 0 }}
                                                        aria-label="New category"
                                                    />
                                                )}
                                            </span>
                                        </td>
                                        <td className="px-3 py-2">
                                            {item.kind === 'open-request' ? (
                                                <select
                                                    className="input-base text-xs"
                                                    value={item.included ? 'draft' : 'ignore'}
                                                    onChange={event => {
                                                        const include = event.target.value === 'draft';
                                                        setItems(current => current.map((currentItem, i) =>
                                                            i === index ? { ...currentItem, included: include } : currentItem));
                                                    }}
                                                    aria-label={`Action for ${item.contentName}`}
                                                >
                                                    <option value="ignore">Ignore</option>
                                                    <option value="draft">Submit draft</option>
                                                </select>
                                            ) : (
                                                <span style={{ color: item.kind === 'edit' ? 'var(--success)' : item.kind === 'new' ? 'var(--accent)' : 'var(--danger)' }}>
                                                    {item.kind === 'edit' ? 'Edit' : item.kind === 'new' ? 'New' : 'Blocked'}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 text-xs" style={{ color: 'var(--text-tertiary)' }}>{item.note}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {(phase === 'submitting' || phase === 'done') && (
                <div className="flex flex-col gap-3">
                    {phase === 'done' && (
                        <div className="flex gap-2">
                            <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--success-soft)', color: 'var(--success)' }}>
                                <CheckCircle2 size={14} /> {doneCount} submitted
                            </span>
                            {errorCount > 0 && (
                                <span className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm" style={{ background: 'var(--danger-soft)', color: 'var(--danger)' }}>
                                    <XCircle size={14} /> {errorCount} failed
                                </span>
                            )}
                        </div>
                    )}
                    <div className="overflow-auto rounded-lg" style={{ maxHeight: '440px', border: '1px solid var(--border-default)' }}>
                        {items.filter(item => item.included && item.kind !== 'blocked').map(item => (
                            <div key={item.relativePath} className="flex items-center gap-3 px-3 py-2 text-sm" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                <span className="font-mono text-xs flex-1 truncate">{item.relativePath}</span>
                                {item.status === 'pending' && <span style={{ color: 'var(--text-tertiary)' }}>Waiting…</span>}
                                {item.status === 'submitting' && <span className="flex items-center gap-1" style={{ color: 'var(--accent)' }}><Loader2 size={13} className="animate-spin" /> Submitting…</span>}
                                {item.status === 'done' && <span className="flex items-center gap-1" style={{ color: 'var(--success)' }}><CheckCircle2 size={13} /> Submitted</span>}
                                {item.status === 'error' && <span className="flex items-center gap-1" style={{ color: 'var(--danger)' }} title={item.error}><AlertCircle size={13} /> {item.error}</span>}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </DialogBase>
    );
};

export default ContentRequestDialog;
